import * as React from 'react';
import { useCallback, useEffect, useState } from 'react';
import Button from '@mui/material/Button';
import {
    Box,
    CircularProgress,
    FormControl,
    FormControlLabel,
    Grid,
    InputLabel,
    MenuItem,
    Select,
    Stack,
    Switch,
    Typography
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { getPreliminaryCalculationResults, recalculateMatch } from '../Api';
import PreliminaryResultDto from '../models/dtos/PreliminaryResultDto';
import { SportNames } from '../models/SportNames';
import { DataGrid, GridColDef, GridRowParams, GridValueGetterParams } from '@mui/x-data-grid';

export const formatRevenue = (revenue: number) => {
    return revenue.toLocaleString('en-US', {
        style: 'currency', currency: 'USD',
    });
}

export default function PreliminaryResultsPage() {
    const navigate = useNavigate();

    const [results, setResults] = useState<PreliminaryResultDto[]>([]);
    const [loading, setLoading] = useState(false);
    const [rowCount, setRowCount] = useState(0);

    const [paginationModel, setPaginationModel] = useState({
        page: 0,
        pageSize: 100,
    });

    const [selectedSportId, setSelectedSportId] = useState<number>(0);
    const [onlyBad, setOnlyBad] = useState<boolean>(false);
    const [hidePending, setHidePending] = useState<boolean>(false);

    const getStateFromInt = (state: number): string => {
        switch (state) {
            case 2: return "PENDING";
            case 4: return "WON";
            case 8: return "LOST";
            default: return "RETURNED";
        }
    };

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const offset = paginationModel.page * paginationModel.pageSize;
            const sportIdToSend = selectedSportId === 0 ? undefined : selectedSportId;
            const resultPage = await getPreliminaryCalculationResults(offset, paginationModel.pageSize, sportIdToSend, onlyBad, hidePending);
            setResults(resultPage?.items ?? []);
            setRowCount(resultPage?.totalItems ?? 0);
        } finally {
            setLoading(false);
        }
    }, [paginationModel, selectedSportId, onlyBad, hidePending]);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const onRecalculateRequested = async (varietyId: number) => {
        await recalculateMatch(varietyId, 3);
        await fetchData();
    }

    const handleSportSelectionChanged = (sportId: number) => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setSelectedSportId(sportId);
    };

    const onActiveChanged = (checked: boolean) => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setOnlyBad(checked);
    };

    const onHidePendingChanged = (checked: boolean) => {
        setPaginationModel((prev) => ({ ...prev, page: 0 }));
        setHidePending(checked);
    };

    const onRowClick = (source : string, varietyId : string) => {
        navigate('/provider/' + source + '/varieties/' + varietyId);
    }

    const columns: GridColDef[] = [
        {
            field: 'matchId', headerName: 'Match', width: 96,
            renderCell: (params) =>
                <Button variant="text" fullWidth={true} style={{ height: '32px' }}
                    onClick={() => navigate('/matches/' + params.row.matchId)}>{params.row.matchId}</Button>
        },
        { field: 'sportName', headerName: 'Sport', width: 100 },
        { field: 'homeTeamName', headerName: 'Home Team', width: 130 },
        { field: 'awayTeamName', headerName: 'Away Team', width: 130 },
        { field: 'leagueName', headerName: 'League', width: 130 },
        {
            field: 'varietyName', headerName: 'Event', width: 220,
            renderCell: (params) =>
                <Button variant="text" fullWidth={true} sx={{
                    maxWidth: '100%',
                    overflow: 'hidden',
                    whiteSpace: 'nowrap',
                    textOverflow: 'ellipsis',
                    textTransform: 'none', // Optional: keeps original casing
                    display: 'inline-block',
                    minWidth: 0, // Important for ellipsis
                    padding: '6px 8px', // Optional, for tighter fit
                  }}
                    onClick={() => onRowClick(params.row.syncSource, params.row.varietySyncId)}>{params.row.varietyName}</Button>
        },
        { field: 'betsCount', headerName: 'Bets', width: 81 },
        { field: 'betName', headerName: 'Bet Name', width: 130 },
        {
            field: 'preliminaryResult',
            headerName: 'Preliminary',
            width: 130,
            valueGetter: (params: GridValueGetterParams) => `${getStateFromInt(params.row.preliminaryState)} (${params.row.preliminaryScoreHome} - ${params.row.preliminaryScoreAway}) `
        },
        {
            field: 'finalResult',
            headerName: 'Final',
            width: 130,
            valueGetter: (params: GridValueGetterParams) => `${getStateFromInt(params.row.finalState)} (${params.row.finalScoreHome} - ${params.row.finalScoreAway}) `
        },
        {
            field: 'recalculated', headerName: 'Recalculation', width: 130,
            renderCell: (params) => params.row.finalState == params.row.preliminaryState || params.row.finalState == 2 ? <Typography variant="body1">-</Typography> : params.row.hasPendingRecalculationRequest ? <Typography variant="body1">Pending...</Typography> :
                params.row.recalculated ? <Typography variant="body1">Recalculated</Typography> :
                    <Button variant="contained" fullWidth={true} style={{ height: '32px' }}
                        onClick={() => onRecalculateRequested(params.row.varietyId)}>Recalc!</Button>
        },
        {
            field: 'calculatedAt',
            headerName: 'Calculated At',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => new Date(params.row.calculatedAt).toLocaleString()
        },
        {
            field: 'matchStartedAt',
            headerName: 'Start Date',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => new Date(params.row.matchStartedAt).toLocaleString()
        },
        {
            field: 'reason',
            headerName: 'Reason',
            width: 200,
            valueGetter: (params: GridValueGetterParams) => params.row.reason == '1' ? 'Score updated' : 'Closed'
        },
        {
            field: 'preliminaryResultRaw',
            headerName: 'Preliminary Result Raw',
            width: 200
        },
        {
            field: 'finalResultRaw',
            headerName: 'Final Result Raw',
            width: 200
        }
    ];

    return (
        <Stack gap="50px 50px" sx={{ mx: 5, my: 5 }} justifyContent='center' alignItems='center'>
            <Grid container spacing={2} columns={{ xs: 1, sm: 2, md: 4, lg: 5 }} justifyContent="flex-end">
                <Grid item xs={1} key="matchesButton" alignContent="center">
                    <Button variant="outlined" fullWidth style={{ height: '54px' }} onClick={() => navigate('/matches')}>
                        Back to matches
                    </Button>
                </Grid>
            </Grid>

            <Typography variant="h3">Preliminary calculation results</Typography>

            {loading && (
                <CircularProgress style={{ width: '48px', height: '48px' }} />
            )}

            <Grid container spacing={1} columns={{ xs: 1, sm: 1, md: 2, lg: 3, xl: 4 }} alignItems='center'>
                <Grid item xs={1} key="funnel" alignContent="center">
                    <FormControl fullWidth>
                        <InputLabel>Select sport</InputLabel>
                        <Select
                            fullWidth
                            value={selectedSportId}
                            onChange={e => handleSportSelectionChanged(parseInt(e.target.value as string))}
                            label="Sport"
                        >
                            {Object.entries(SportNames).map(([id, name]) => (
                                <MenuItem key={id} value={id}>{name}</MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                </Grid>
                <Grid item xs={1} key="onlyBad" alignContent="center">
                    <FormControlLabel
                        control={<Switch checked={onlyBad} onChange={e => onActiveChanged(e.target.checked)} />}
                        label="Show only failed"
                    />
                </Grid>
                <Grid item xs={1} key="hidePending" alignContent="center">
                    <FormControlLabel
                        control={<Switch checked={hidePending} onChange={e => onHidePendingChanged(e.target.checked)} />}
                        label="Hide pending"
                    />
                </Grid>
            </Grid>

            <DataGrid
                sx={{
                    width: '100%',
                    "& .pending": { backgroundColor: "lightYellow" },
                    "& .confirmed": { backgroundColor: "lightGreen" },
                    "& .failed": { backgroundColor: "lightCoral" },
                }}
                getRowClassName={(params) =>
                    params.row.finalState === 2
                        ? "pending"
                        : params.row.finalState !== params.row.preliminaryState && !params.row.recalculated
                            ? "failed"
                            : "confirmed"
                }
                rows={results}
                columns={columns}
                paginationMode="server"
                rowCount={rowCount}
                paginationModel={paginationModel}
                onPaginationModelChange={setPaginationModel}
                loading={loading}
                pageSizeOptions={[25, 50, 100]}
                disableRowSelectionOnClick
            />
        </Stack>
    );
}